import { Desdobramento } from '../types/negocio/Desdobramento';
import api from './api';
import BaseService from './BaseService';
import { planoAcaoService } from './PlanoAcaoService';

class DesdobramentoService extends BaseService<Desdobramento> {
  constructor() {
    super('desdobramento', api);
  }

  /**
   * Lista todos os desdobramentos vinculados a um plano de ação específico.
   * @param planoAcaoGuid GUID do plano de ação
   * @param filtros Filtros opcionais (responsável, intervalo de datas, etc.)
   * @returns Promise com os desdobramentos
   */
  async listarPorPlano(
    planoAcaoGuid: string,
    filtros?: {
      responsavel?: string;
    }
  ): Promise<Desdobramento[]> {
    try {
      const params = { ...filtros };
      const response = await this.api.get(`/api/${this.route}/plano/${planoAcaoGuid}`, { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar desdobramentos por plano:', error);
      throw error;
    }
  }

  /**
   * Busca desdobramentos com filtros opcionais.
   * Permite buscar tarefas por responsável sem necessidade de selecionar um plano de ação.
   * @param filtros Filtros opcionais (planoAcaoGuid, responsável)
   * @returns Promise com os desdobramentos (incluindo nome do plano quando não filtrado por plano)
   */
  async buscarComFiltros(
    filtros?: {
      planoAcaoGuid?: string;
      responsavel?: string;
    }
  ): Promise<Desdobramento[]> {
    try {
      const params = { ...filtros };
      const response = await this.api.get(`/api/${this.route}/buscar`, { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar desdobramentos:', error);
      throw error;
    }
  }

  /**
   * Atualiza a situação do kanban de um desdobramento específico.
   * @param guid GUID do desdobramento
   * @param situacaoKanban Nova situação do kanban
   * @returns Promise com o desdobramento atualizado e indicador se o plano foi finalizado
   */
  async atualizarSituacaoKanban(guid: string, situacaoKanban: string): Promise<{ tarefa: Desdobramento, planoFinalizado: boolean }> {
    const response = await this.api.patch(`/api/${this.route}/${guid}/situacao-kanban`, { situacaoKanban });
    const tarefaAtualizada = response.data;
    
    // Verificar e atualizar status do plano se necessário
    let planoFinalizado = false;
    if (tarefaAtualizada.planoAcaoGuid) {
      planoFinalizado = await this.verificarEAtualizarStatusPlano(tarefaAtualizada.planoAcaoGuid);
    }
    
    return { tarefa: tarefaAtualizada, planoFinalizado };
  }

  /**
   * Verifica o status das tarefas de um plano de ação e atualiza o status do plano conforme necessário.
   * Regras:
   * - Todas finalizadas → Plano "Finalizada"
   * - Pelo menos uma "Em Andamento" ou "Finalizada" → Plano "Em Andamento"
   * - Todas "Pendente" → Plano "Pendente"
   * @param planoAcaoGuid GUID do plano de ação
   * @returns Promise<boolean> indicando se o plano foi finalizado
   */
  async verificarEAtualizarStatusPlano(planoAcaoGuid: string): Promise<boolean> {
    try {
      // Buscar todas as tarefas do plano de ação
      const tarefas = await this.listarPorPlano(planoAcaoGuid);
      
      // Se não houver tarefas, não faz nada
      if (!tarefas || tarefas.length === 0) {
        return false;
      }

      // Verificar status das tarefas
      const todasFinalizadas = tarefas.every(
        (tarefa) => tarefa.situacaoKanban === 'Finalizada'
      );
      
      const todasPendentes = tarefas.every(
        (tarefa) => tarefa.situacaoKanban === 'Pendente'
      );
      
      const algumEmAndamentoOuFinalizada = tarefas.some(
        (tarefa) => tarefa.situacaoKanban === 'Em Andamento' || tarefa.situacaoKanban === 'Finalizada'
      );

      // Buscar o plano atual para verificar o status
      const planoAtual = await planoAcaoService.buscarPorId(planoAcaoGuid);
      let statusAtualizado = false;

      // Atualizar status do plano conforme as regras
      if (todasFinalizadas && planoAtual.status !== 'Finalizada') {
        await planoAcaoService.atualizarStatus(planoAcaoGuid, 'Finalizada');
        console.log(`Plano de ação ${planoAcaoGuid} atualizado para Finalizada`);
        return true; // Retorna true apenas quando finalizado
      } else if (algumEmAndamentoOuFinalizada && planoAtual.status !== 'Em Andamento' && planoAtual.status !== 'Finalizada') {
        await planoAcaoService.atualizarStatus(planoAcaoGuid, 'Em Andamento');
        console.log(`Plano de ação ${planoAcaoGuid} atualizado para Em Andamento`);
      } else if (todasPendentes && planoAtual.status !== 'Pendente') {
        await planoAcaoService.atualizarStatus(planoAcaoGuid, 'Pendente');
        console.log(`Plano de ação ${planoAcaoGuid} atualizado para Pendente`);
      }
      
      return false;
    } catch (error) {
      console.error('Erro ao verificar e atualizar status do plano:', error);
      // Não lança o erro para não afetar a operação principal de atualização da tarefa
      return false;
    }
  }

  /**
   * Finaliza todas as tarefas de um plano de ação
   * @param planoAcaoGuid GUID do plano de ação
   */
  async finalizarTodasTarefasDoPlano(planoAcaoGuid: string): Promise<void> {
    try {
      // Buscar todas as tarefas do plano de ação
      const tarefas = await this.listarPorPlano(planoAcaoGuid);
      
      // Se não houver tarefas, não faz nada
      if (!tarefas || tarefas.length === 0) {
        return;
      }

      // Atualizar cada tarefa que não esteja finalizada para "Finalizada"
      const promessas = tarefas
        .filter(tarefa => tarefa.situacaoKanban !== 'Finalizada')
        .map(tarefa => 
          this.api.patch(`/api/${this.route}/${tarefa.guid}/situacao-kanban`, { 
            situacaoKanban: 'Finalizada' 
          })
        );

      await Promise.all(promessas);
      console.log(`Todas as tarefas do plano ${planoAcaoGuid} foram finalizadas`);
    } catch (error) {
      console.error('Erro ao finalizar tarefas do plano:', error);
      throw error;
    }
  }

  /**
   * Marca todas as tarefas de um plano de ação como Pendente
   * @param planoAcaoGuid GUID do plano de ação
   */
  async marcarTodasTarefasComoPendente(planoAcaoGuid: string): Promise<void> {
    try {
      // Buscar todas as tarefas do plano de ação
      const tarefas = await this.listarPorPlano(planoAcaoGuid);
      
      // Se não houver tarefas, não faz nada
      if (!tarefas || tarefas.length === 0) {
        return;
      }

      // Atualizar cada tarefa que não esteja pendente para "Pendente"
      const promessas = tarefas
        .filter(tarefa => tarefa.situacaoKanban !== 'Pendente')
        .map(tarefa => 
          this.api.patch(`/api/${this.route}/${tarefa.guid}/situacao-kanban`, { 
            situacaoKanban: 'Pendente' 
          })
        );

      await Promise.all(promessas);
      console.log(`Todas as tarefas do plano ${planoAcaoGuid} foram marcadas como Pendente`);
    } catch (error) {
      console.error('Erro ao marcar tarefas como pendente:', error);
      throw error;
    }
  }

  /**
   * Sobrescreve o método atualizar para incluir verificação automática do status do plano
   */
  async atualizar(data: Desdobramento): Promise<Desdobramento> {
    const tarefaAtualizada = await super.atualizar(data);
    
    // Verificar e atualizar status do plano se necessário
    if (data.planoAcaoGuid) {
      await this.verificarEAtualizarStatusPlano(data.planoAcaoGuid);
    }
    
    return tarefaAtualizada;
  }

}

export const desdobramentoService = new DesdobramentoService();